import { AxiosMethods } from '@setvi/shared/enums';

import type {
  PdvAttrAction,
  PdvAttrPreview,
  PdvClassDetail,
  PdvFlagFilters,
  PdvFlagPage,
  PdvOverview,
  PdvProductPage,
  PdvNotScored,
  PdvProductFeedbackContext,
  PdvSearchResult,
  PdvSegmentNode
} from './types';
import { pdvCall as call, PDV_PAGE_SIZE, pdvNextOffset } from './client';
import { pdvKeys } from './keys';

export * from './client';
export * from './keys';
export * from './types';
export * from './use-identity';

/*
 * Product Data Verification — served by revops-api, behind the same JWT as every other
 * route. The standalone service this replaces had no authentication at all.
 *
 * Single-item reads (overview, tree, class, product) return what the MVP returned.
 * The lists page the way every other revops-api list does — a POST carrying `limit`
 * and `offset`, answering `{ data, total, limit }`. Free text is `searchStr`, not `q`.
 *
 * Two things that are easy to get wrong:
 *
 *   A class is never a path segment. Six CFS class names contain a slash ("18/8
 *   Flatware", "Accessory/Parts-Catalog") and an encoded slash never reaches the API.
 *   GET /pdv/class takes it as a query param; the lists carry it in the body.
 *
 *   `by` is not read from a request. Every flag and decision is attributed to the
 *   signed-in user from the token, which a caller cannot spoof. usePdvIdentity is
 *   still what the UI shows a name from.
 *
 * Writes live in Services/Mutations/Pdv.
 */

const FIVE_MINUTES = 5 * 60 * 1000;

/** Caps the API enforces anyway; sending more just gets cut to these. */
export const PDV_LIMITS = {
  search: 100,
  flags: 200
} as const;

export const pdvOverviewQuery = (completeAt?: number) => ({
  queryKey: [...pdvKeys.overview(), completeAt ?? 'default'] as const,
  queryFn: () =>
    call<PdvOverview>('/pdv/overview', AxiosMethods.GET, {
      params: { completeAt }
    }),
  staleTime: FIVE_MINUTES
});

export const pdvTreeQuery = (completeAt?: number) => ({
  queryKey: [...pdvKeys.tree(), completeAt ?? 'default'] as const,
  queryFn: () =>
    call<PdvSegmentNode[]>('/pdv/tree', AxiosMethods.GET, {
      params: { completeAt }
    }),
  staleTime: FIVE_MINUTES
});

export const pdvSearchQuery = (q: string, limit = 25) => ({
  queryKey: pdvKeys.search(q),
  queryFn: () =>
    call<PdvSearchResult>('/pdv/search', AxiosMethods.POST, {
      body: {
        searchStr: q,
        limit: Math.min(limit, PDV_LIMITS.search),
        offset: 0
      }
    }),
  enabled: q.trim().length > 1,
  keepPreviousData: true
});

export const pdvClassQuery = (cls: string, completeAt?: number) => ({
  queryKey: [...pdvKeys.class(cls), completeAt ?? 'default'] as const,
  queryFn: () =>
    call<PdvClassDetail>('/pdv/class', AxiosMethods.GET, {
      params: { cls, completeAt }
    }),
  enabled: Boolean(cls)
});

/**
 * A class's products, worst first, one page at a time — for useInfiniteQuery, so
 * "Load more" fetches only the next page instead of everything again.
 */
export const pdvClassProductsInfiniteQuery = (cls: string, q = '') => ({
  queryKey: pdvKeys.classProducts(cls, q),
  queryFn: ({ pageParam = 0 }: { pageParam?: number }) =>
    call<PdvProductPage>('/pdv/class/products', AxiosMethods.POST, {
      body: {
        cls,
        limit: PDV_PAGE_SIZE,
        offset: pageParam,
        ...(q ? { searchStr: q } : {})
      }
    }),
  // typed here: passed bare, the generic helper would set the page type to unknown[]
  getNextPageParam: (last: PdvProductPage, pages: PdvProductPage[]) =>
    pdvNextOffset(last, pages),
  enabled: Boolean(cls),
  keepPreviousData: true
});

/**
 * What a decision would do to the class's complete count, for the confirmation
 * dialog. Rejecting a proposal returns the same number twice: proposals don't score.
 */
export const pdvAttrPreviewQuery = (
  cls: string,
  name: string,
  action: PdvAttrAction | '',
  completeAt?: number
) => ({
  queryKey: [...pdvKeys.preview(cls, name, action), completeAt ?? 'default'],
  queryFn: () =>
    call<PdvAttrPreview>('/pdv/class/attributes/preview', AxiosMethods.GET, {
      params: { cls, name, action, completeAt }
    }),
  enabled: Boolean(cls && name && action),
  retry: false
});

/**
 * What the feedback control needs: the product's attributes, present and missing.
 *
 * An unscored product answers 404 carrying its reason and its flags — that is an
 * answer, not a failure, and three quarters of the catalogue is unscored. axios
 * throws on 404 by default, so this one call accepts it as a success.
 */
export const pdvProductQuery = (sku: string) => ({
  queryKey: pdvKeys.product(sku),
  queryFn: () =>
    call<PdvProductFeedbackContext | PdvNotScored>(
      '/pdv/product',
      AxiosMethods.GET,
      {
        params: { sku },
        validateStatus: (s: number) => s === 200 || s === 404
      }
    ),
  enabled: Boolean(sku),
  retry: false
});

/**
 * The screens use "all" as the unfiltered value of a dropdown. The API has no such
 * value — it reads an absent field as "every one" and a present one as a literal to
 * match, so `brand: "all"` would hunt for a brand named "all" and find nothing.
 */
const set = (key: string, value?: string) =>
  value && value !== 'all' ? { [key]: value } : {};

const flagsBody = (filters: PdvFlagFilters) => ({
  ...set('status', filters.status),
  ...set('cls', filters.cls),
  ...set('brand', filters.brand),
  ...set('assignedTo', filters.assignedTo),
  ...set('sku', filters.sku),
  ...set('searchStr', filters.q),
  ...set('sortBy', filters.sortBy),
  ...set('sortDir', filters.sortDir)
});

/** One bounded read of the data-issue queue, e.g. every flag on one product. */
export const pdvFlagsQuery = (
  filters: PdvFlagFilters,
  limit: number = PDV_LIMITS.flags
) => ({
  queryKey: pdvKeys.flagList(filters, limit),
  queryFn: () =>
    call<PdvFlagPage>('/pdv/flags/search', AxiosMethods.POST, {
      body: {
        limit: Math.min(limit, PDV_LIMITS.flags),
        offset: 0,
        ...flagsBody(filters)
      }
    }),
  keepPreviousData: true
});

/**
 * The shared data-issue queue, paged for useInfiniteQuery. Filters mirror how a
 * product manager works it; counts and the brand / class lists ride on every page.
 */
export const pdvFlagsInfiniteQuery = (filters: PdvFlagFilters) => ({
  queryKey: pdvKeys.flagList(filters),
  queryFn: ({ pageParam = 0 }: { pageParam?: number }) =>
    call<PdvFlagPage>('/pdv/flags/search', AxiosMethods.POST, {
      body: { limit: PDV_PAGE_SIZE, offset: pageParam, ...flagsBody(filters) }
    }),
  getNextPageParam: (last: PdvFlagPage, pages: PdvFlagPage[]) =>
    pdvNextOffset(last, pages),
  keepPreviousData: true
});
